import { useCallback, useMemo, useRef } from 'react'
import { hashKey, useQueryClient, type QueryKey } from '@tanstack/react-query'

/**
 * #336 — an expired thumbnail heals once. Thumbnail links are presigned S3 links that live 1 hour and come from a
 * 55-minute server cache, so a link can arrive with 5 minutes left; a list that is not refetched then shows broken
 * pictures. The first `<img>` error of a list refreshes that list (its rows come back with fresh links). Every card
 * of a list whose links expired fails at the same moment, so further errors for that list within the minute do
 * nothing. The minute is shared by every component showing the list (one module-level map: list key → last time).
 * Each place keeps its own error look; this only asks for fresh links.
 */
export const IMAGE_RETRY_GAP_MS = 60_000

const lastRetryAt = new Map<string, number>()

/** For tests: forget every list's last refresh. */
export function resetImageRetry(): void {
  lastRetryAt.clear()
}

export function useImageRetry(listKey: QueryKey): { onError: () => void } {
  const queryClient = useQueryClient()
  // The key is often a new array on every render (filters objects); its hash keeps onError the same function, so a
  // memoised card is not drawn again.
  const hash = hashKey(listKey)
  const keyRef = useRef(listKey)
  keyRef.current = listKey
  const onError = useCallback(() => {
    const now = Date.now()
    const last = lastRetryAt.get(hash)
    if (last !== undefined && now - last < IMAGE_RETRY_GAP_MS) return
    lastRetryAt.set(hash, now)
    void queryClient.invalidateQueries({ queryKey: keyRef.current })
  }, [queryClient, hash])
  return useMemo(() => ({ onError }), [onError])
}
